
import { useMemo, useRef, useState, useEffect } from "react";
import { Layout, Layouts, Responsive, WidthProvider } from "react-grid-layout";
// @ts-ignore -- side-effect CSS import resolved by bundler
import "react-grid-layout/css/styles.css";
// @ts-ignore -- side-effect CSS import resolved by bundler
import "react-resizable/css/styles.css";
import { WidgetProvider, useRefreshContext, useWidgetContext } from "./widgets/context";
import { WidgetContainer, DefaultSettingsPanel } from "./widgets/container";
import { widgetRegistry, registerWidgets } from "../../services/dashboard/WidgetRegistryService";
import { widgetDefinitions } from "./widgets/definitions";
import useGlobalFilters from "../../hooks/useGlobalFilters";
import {
  GlobalFilters,
  GridLayout,
  WidgetInstance,
  WidgetInstanceConfig,
  WidgetSettings,
} from "../../models/Dashboard";
import { Developer } from "../../models/Developer";
import { Program } from "../../models/Program";
import { MerchantGroup } from "../../models/MerchantGroup";
import { ContactApi } from "../../services/api/ContactApi";
import useContact from "../../hooks/useContact";
import { WidgetManager } from "./widgets/WidgetManager";
import { BREAKPOINT_COLS } from "./widgets/gridPlacement";

const ResponsiveGridLayout = WidthProvider(Responsive);

const EMPTY_LAYOUTS: Layouts = { lg: [], md: [], sm: [], xs: [], xxs: [] };

const sortByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base" }));

const resolveWidgetSettings = (
  definitionId: string,
  persistedSettings?: WidgetSettings | null
): WidgetSettings => {
  const definition = widgetRegistry.get(definitionId);
  const defaultSettings = definition?.defaultSettings ?? {};

  const sanitizedPersistedSettings = Object.entries(persistedSettings ?? {}).reduce<WidgetSettings>(
    (acc, [key, value]) => {
      if (Object.prototype.hasOwnProperty.call(defaultSettings, key)) {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );

  return {
    ...defaultSettings,
    ...sanitizedPersistedSettings,
  };
};

const Dashboard = () => {
  useEffect(() => {
    registerWidgets(widgetDefinitions);
  }, []);

  return (
    <WidgetProvider layoutId="default">
      <DashboardContent />
    </WidgetProvider>
  );
};

const DashboardContent = () => {
  const {
    layouts,
    globalFilters,
    updateGlobalFilters,
    updateWidgetSettings,
    saveWidgetSettings,
    refreshWidget,
    updateLayout,
    hideWidget,
  } =
    useWidgetContext();

  const [activeSettingsWidgetId, setActiveSettingsWidgetId] = useState<string | null>(null);
  const [draftSettings, setDraftSettings] = useState<WidgetSettings | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { developers } = useContact() as { developers: Developer[] };
  const [selectedDeveloperUniqueTag, setSelectedDeveloperUniqueTag] = useState("");
  const [developerPrograms, setDeveloperPrograms] = useState<Program[]>([]);
  const [selectedProgramUniqueTag, setSelectedProgramUniqueTag] = useState("");
  const [merchantGroups, setMerchantGroups] = useState<MerchantGroup[]>([]);
  const [selectedMerchantGroupUniqueTag, setSelectedMerchantGroupUniqueTag] = useState("");
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [isDeveloperMissing, setIsDeveloperMissing] = useState(false);
  const [developerProgramsError, setDeveloperProgramsError] = useState<string | null>(null);
  const [merchantGroupsError, setMerchantGroupsError] = useState<string | null>(null);
  const [isWidgetManagerOpen, setIsWidgetManagerOpen] = useState(false);

  const isUserInteractingRef = useRef(false);

  const getDeveloperPrograms = async (developerUniqueTag: string) => {
    if (!developerUniqueTag) {
      setDeveloperPrograms([]);
      setSelectedProgramUniqueTag("");
      setMerchantGroups([]);
      setSelectedMerchantGroupUniqueTag("");
      return;
    }

    try {
      const response = await ContactApi.getDeveloperPrograms(developerUniqueTag);
      const programs = response.data;

      if (!Array.isArray(programs)) {
        setDeveloperPrograms([]);
        setDeveloperProgramsError(null);
        return;
      }

      const sortedPrograms = sortByName(programs);
      setDeveloperPrograms(sortedPrograms);
      setDeveloperProgramsError(null);

      const validProgramUniqueTags = new Set(
        sortedPrograms.map((program) => program.uniqueTag)
      );
      setSelectedProgramUniqueTag((previous) =>
        validProgramUniqueTags.has(previous) ? previous : ""
      );
    } catch (error) {
      console.error("Failed to load developer programs:", error);
      setDeveloperPrograms([]);
      setDeveloperProgramsError(
        "Failed to load programs. Please try selecting a different developer."
      );
    }
  };

  const getMerchantGroups = async (programUniqueTag: string) => {
    if (!selectedDeveloperUniqueTag || !programUniqueTag) {
      setMerchantGroups([]);
      setSelectedMerchantGroupUniqueTag("");
      return;
    }

    try {
      const response = await ContactApi.getProgramMerchantGroups(programUniqueTag);
      const merged = response.data as MerchantGroup[];
      const unique = Array.from(
        new Map(merged.map((item) => [item.uniqueTag, item])).values()
      );
      const sortedMerchantGroups = sortByName(unique);
      setMerchantGroups(sortedMerchantGroups);
      setMerchantGroupsError(null);

      const validMerchantGroupUniqueTags = new Set(
        sortedMerchantGroups.map((item) => item.uniqueTag)
      );
      setSelectedMerchantGroupUniqueTag((previous) =>
        validMerchantGroupUniqueTags.has(previous) ? previous : ""
      );
    } catch (error) {
      console.error("Failed to load merchant groups:", error);
      setMerchantGroupsError(
        "Failed to load merchant groups. Please try selecting a different program."
      );
    }
  };

  useEffect(() => {
    void getDeveloperPrograms(selectedDeveloperUniqueTag);
  }, [selectedDeveloperUniqueTag]);

  const activeWidgetConfig = activeSettingsWidgetId
    ? layouts?.widgets.find((w) => w.widgetId === activeSettingsWidgetId) ?? null
    : null;
  const activeDefinition = activeWidgetConfig
    ? widgetRegistry.get(activeWidgetConfig.definitionId) ?? null
    : null;

  const handleOpenSettings = (widgetId: string) => {
    setIsWidgetManagerOpen(false);
    const widgetConfig = layouts?.widgets.find((w) => w.widgetId === widgetId);
    setActiveSettingsWidgetId(widgetId);
    setDraftSettings(
      resolveWidgetSettings(widgetConfig?.definitionId ?? "", widgetConfig?.settings)
    );
  };

  const handleApplySettings = async () => {
    if (!activeSettingsWidgetId || !draftSettings) return;
    try {
      setIsSaving(true);
      await updateWidgetSettings(activeSettingsWidgetId, draftSettings);
      await saveWidgetSettings(activeSettingsWidgetId, draftSettings);
      setActiveSettingsWidgetId(null);
      setDraftSettings(null);
    } catch (error) {
      console.error('Failed to save widget settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSettings = () => {
    setActiveSettingsWidgetId(null);
    setDraftSettings(null);
  };

  useEffect(() => {
    const savedDeveloperTag = globalFilters.entities?.developerUniqueTags?.[0];
    const savedProgramTag = globalFilters.entities?.programUniqueTags?.[0];
    const savedMerchantGroupTag = globalFilters.entities?.merchantGroupUniqueTags?.[0];

    if (!savedDeveloperTag && developers.length > 0) {
      setSelectedDeveloperUniqueTag(developers[0].uniqueTag);
      setIsDeveloperMissing(false);
    } else if (savedDeveloperTag) {
      setSelectedDeveloperUniqueTag(savedDeveloperTag);
      setIsDeveloperMissing(false);
    } else {
      setSelectedDeveloperUniqueTag("");
      setIsDeveloperMissing(true);
    }

    setSelectedProgramUniqueTag(savedProgramTag ?? "");
    setSelectedMerchantGroupUniqueTag(savedMerchantGroupTag ?? "");
  }, [globalFilters, developers]);

  useEffect(() => {
    void getMerchantGroups(selectedProgramUniqueTag);
  }, [selectedDeveloperUniqueTag, selectedProgramUniqueTag]);

  const handleDeveloperFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = event.target.value;
    setSelectedDeveloperUniqueTag(selected);
    setIsDeveloperMissing(!selected);
    setSelectedProgramUniqueTag("");
    setSelectedMerchantGroupUniqueTag("");
  };

  const handleProgramFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = event.target.value;
    setSelectedProgramUniqueTag(selected);
    setSelectedMerchantGroupUniqueTag("");
  };

  const handleMerchantGroupFilterChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selected = event.target.value;
    setSelectedMerchantGroupUniqueTag(selected);
  };

  const buildGuidedGlobalFilters = (): GlobalFilters => {
    const next: GlobalFilters = {};

    const entities: NonNullable<GlobalFilters["entities"]> = {};
    if (selectedDeveloperUniqueTag) {
      entities.developerUniqueTags = [selectedDeveloperUniqueTag];
    }
    if (selectedProgramUniqueTag) {
      entities.programUniqueTags = [selectedProgramUniqueTag];
    }
    if (selectedMerchantGroupUniqueTag) {
      entities.merchantGroupUniqueTags = [selectedMerchantGroupUniqueTag];
    }

    if (Object.keys(entities).length > 0) {
      next.entities = entities;
    }

    return next;
  };

  const appliedFilterSnapshot = {
    developerUniqueTag: globalFilters.entities?.developerUniqueTags?.[0] ?? "",
    programUniqueTag: globalFilters.entities?.programUniqueTags?.[0] ?? "",
    merchantGroupUniqueTag: globalFilters.entities?.merchantGroupUniqueTags?.[0] ?? "",
  };

  const currentFilterSnapshot = {
    developerUniqueTag: selectedDeveloperUniqueTag,
    programUniqueTag: selectedProgramUniqueTag,
    merchantGroupUniqueTag: selectedMerchantGroupUniqueTag,
  };

  const isFiltersDirty =
    appliedFilterSnapshot.developerUniqueTag !== currentFilterSnapshot.developerUniqueTag ||
    appliedFilterSnapshot.programUniqueTag !== currentFilterSnapshot.programUniqueTag ||
    appliedFilterSnapshot.merchantGroupUniqueTag !== currentFilterSnapshot.merchantGroupUniqueTag;

  const hasAnyFilterSelection =
    Boolean(currentFilterSnapshot.developerUniqueTag) ||
    Boolean(currentFilterSnapshot.programUniqueTag) ||
    Boolean(currentFilterSnapshot.merchantGroupUniqueTag) ||
    Boolean(appliedFilterSnapshot.developerUniqueTag) ||
    Boolean(appliedFilterSnapshot.programUniqueTag) ||
    Boolean(appliedFilterSnapshot.merchantGroupUniqueTag);

  const applyGlobalFilters = async () => {
    if (!selectedDeveloperUniqueTag) {
      setIsDeveloperMissing(true);
      console.error("Developer is a required filter");
      return;
    }

    const nextFilters = buildGuidedGlobalFilters();

    try {
      setIsApplyingFilters(true);
      await updateGlobalFilters(nextFilters);
    } catch (error) {
      console.error("Failed to apply global filters:", error);
    } finally {
      setIsApplyingFilters(false);
    }
  };

  const clearGlobalFilters = async () => {
    const nextFilters: GlobalFilters = {};

    try {
      setIsApplyingFilters(true);
      setSelectedDeveloperUniqueTag("");
      setIsDeveloperMissing(true);
      setSelectedProgramUniqueTag("");
      setSelectedMerchantGroupUniqueTag("");
      await updateGlobalFilters(nextFilters);
    } catch (error) {
      console.error("Failed to clear global filters:", error);
    } finally {
      setIsApplyingFilters(false);
    }
  };

    const visibleGridLayouts = useMemo<Layouts>(() => {
      if (!layouts) return EMPTY_LAYOUTS;

      const visibleIds = new Set(
          layouts.widgets.filter((w) => w.isVisible !== false).map((w) => w.widgetId)
      );
      const keepVisible = (items?: GridLayout[]) =>
          (items ?? []).filter((i) => visibleIds.has(i.i));

      const lg = keepVisible(layouts.layouts?.lg);

      return {
          lg: sanitizeLayout(lg),
          md: sanitizeLayout(keepVisible(layouts.layouts?.md).length
              ? keepVisible(layouts.layouts?.md) : adjustLayoutForSize(lg, 10)),
          sm: sanitizeLayout(keepVisible(layouts.layouts?.sm).length
              ? keepVisible(layouts.layouts?.sm) : adjustLayoutForSize(lg, 6)),
          xs: sanitizeLayout(keepVisible(layouts.layouts?.xs).length
              ? keepVisible(layouts.layouts?.xs) : adjustLayoutForSize(lg, 4)),
          xxs: sanitizeLayout(keepVisible(layouts.layouts?.xxs).length
              ? keepVisible(layouts.layouts?.xxs) : adjustLayoutForSize(lg, 2)),
      };
  }, [layouts]);

  if (!layouts) {
    return <div>Loading dashboard...</div>;
  }

  const visibleWidgets = layouts.widgets.filter((w) => w.isVisible !== false);

  return (
    <div className="report-container">
      <div className="report-content">
        <section
          className={`dashboard-global-filters dashboard-topbar ${isFiltersDirty ? "dashboard-global-filters--dirty" : ""}`}
          aria-label="Global dashboard filters"
        >
          <div className="dashboard-global-filters__header dashboard-topbar__header">
            <div className="dashboard-topbar__title-wrap">
              <h5 className="dashboard-topbar__title mb-0">Dashboard</h5>
              <span className="dashboard-topbar__subtitle">Global Filters</span>
              {isDeveloperMissing && (
                <span className="dashboard-topbar__dirty-badge dashboard-topbar__required-badge">Required field missing</span>
              )}
              {developerProgramsError && (
                <span className="dashboard-topbar__dirty-badge dashboard-topbar__required-badge">{developerProgramsError}</span>
              )}
              {merchantGroupsError && (
                <span className="dashboard-topbar__dirty-badge dashboard-topbar__required-badge">{merchantGroupsError}</span>
              )}
              {!isDeveloperMissing && !developerProgramsError && !merchantGroupsError && isFiltersDirty && (
                <span className="dashboard-topbar__dirty-badge">Unsaved changes</span>
              )}
            </div>
            <p className="dashboard-topbar__hint mb-0">
              Choose a developer first. Program and merchant group will load for that developer.
            </p>
          </div>

          <div className="dashboard-global-filters__row dashboard-global-filters__row--single-line">
            <div>
              <label className="form-label mb-1" htmlFor="dashboard-filter-developers">
                Developer <span style={{ color: "red" }}>*</span>
              </label>
              <select
                id="dashboard-filter-developers"
                className={`form-select form-select-sm ${isDeveloperMissing ? "is-invalid" : ""}`}
                value={selectedDeveloperUniqueTag}
                onChange={handleDeveloperFilterChange}
                required
              >
                <option value="">Select a developer</option>
                {developers.map((developer) => (
                  <option key={developer.uniqueTag} value={developer.uniqueTag}>
                    {developer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label mb-1" htmlFor="dashboard-filter-programs">
                Program
              </label>
              <select
                id="dashboard-filter-programs"
                className="form-select form-select-sm"
                value={selectedProgramUniqueTag}
                onChange={handleProgramFilterChange}
                disabled={!selectedDeveloperUniqueTag}
              >
                <option value="">
                  {!selectedDeveloperUniqueTag ? "Select Developer first" : "Select a program"}
                </option>
                {developerPrograms.map((program) => (
                  <option key={program.uniqueTag} value={program.uniqueTag}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label mb-1" htmlFor="dashboard-filter-merchant-groups">
                Merchant Group
              </label>
              <select
                id="dashboard-filter-merchant-groups"
                className="form-select form-select-sm"
                value={selectedMerchantGroupUniqueTag}
                onChange={handleMerchantGroupFilterChange}
                disabled={!selectedProgramUniqueTag}
              >
                <option value="">
                  {!selectedProgramUniqueTag ? "Select Program first" : "Select a merchant group"}
                </option>
                {merchantGroups.map((group) => (
                  <option key={group.uniqueTag} value={group.uniqueTag}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="dashboard-global-filters__actions">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={clearGlobalFilters}
                disabled={isApplyingFilters || !hasAnyFilterSelection}
                type="button"
              >
                Clear
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={applyGlobalFilters}
                disabled={isApplyingFilters || !isFiltersDirty || !selectedDeveloperUniqueTag}
                title={!selectedDeveloperUniqueTag ? "Developer is required" : ""}
                type="button"
              >
                {isApplyingFilters ? "Applying..." : "Apply"}
              </button>

              <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => {
                      handleCancelSettings();
                      setIsWidgetManagerOpen(true);
                  }}
              >
                  <i className="bi bi-grid-1x2 me-1" aria-hidden="true" />
                  Manage widgets
              </button>

            </div>
          </div>

        </section>

        <ResponsiveGridLayout
          className="dashboard-grid"
          layouts={visibleGridLayouts}
          cols={BREAKPOINT_COLS} 
          rowHeight={28}
          draggableHandle=".widget-drag"
          onDragStart={() => { isUserInteractingRef.current = true; }}
          onResizeStart={() => { isUserInteractingRef.current = true; }}
          onLayoutChange={(_layout: Layout[], nextLayouts: Layouts) => {
              if (isUserInteractingRef.current) {
                  isUserInteractingRef.current = false;
                  void updateLayout(nextLayouts);
              }
          }}
        >
          {visibleWidgets.map((widgetConfig) => (
            <div
              key={widgetConfig.widgetId}
              className="dashboard-widget"
            >
              <DashboardWidgetRenderer
                widgetConfig={widgetConfig}
                onRefresh={() => refreshWidget(widgetConfig.widgetId)}
                onOpenSettings={() => handleOpenSettings(widgetConfig.widgetId)}
                onRemove={() => hideWidget(widgetConfig.widgetId)}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>

      <div
        className={`dashboard-settings-backdrop ${activeSettingsWidgetId ? "open" : ""}`}
        onClick={handleCancelSettings}
      />
      <aside className={`dashboard-settings-drawer ${activeSettingsWidgetId ? "open" : ""}`}>
        <div className="settings-drawer-header">
          <h6 className="mb-0">{activeDefinition?.title ?? ""} Settings</h6>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={handleCancelSettings}
          >
            Close
          </button>
        </div>
        <div className="settings-drawer-content">
          {activeDefinition && draftSettings && (
            activeDefinition.SettingsPanel ? (
              <activeDefinition.SettingsPanel
                settings={draftSettings as any}
                onSettingsChange={(newSettings) => setDraftSettings(newSettings)}
                isSaving={isSaving}
              />
            ) : (
              <DefaultSettingsPanel
                settings={draftSettings}
                defaultSettings={activeDefinition.defaultSettings}
                onSettingsChange={setDraftSettings}
                isSaving={isSaving}
              />
            )
          )}
        </div>
        <div className="settings-drawer-footer">
          <button
            className="btn btn-outline-secondary"
            onClick={handleCancelSettings}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleApplySettings}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Apply"}
          </button>
        </div>
      </aside>

      <div
          className={`dashboard-settings-backdrop ${isWidgetManagerOpen ? "open" : ""}`}
          onClick={() => setIsWidgetManagerOpen(false)}
      />
      <aside className={`dashboard-settings-drawer ${isWidgetManagerOpen ? "open" : ""}`}>
          <div className="settings-drawer-header">
              <h6 className="mb-0">Widget Manager</h6>
              <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setIsWidgetManagerOpen(false)}
              >
                  Close
              </button>
          </div>
          <div className="settings-drawer-content">
              {isWidgetManagerOpen && <WidgetManager/>}
          </div>
          <div className="settings-drawer-footer">
              <button
              className="btn btn-primary"
              onClick={() => setIsWidgetManagerOpen(false)}
              >
              Done
          </button>
      </div>
      </aside>

    </div>
  );
};
interface DashboardWidgetRendererProps {
  widgetConfig: WidgetInstanceConfig;
  onRefresh: () => void;
  onOpenSettings: () => void;
  onRemove: () => void;
}

const DashboardWidgetRenderer = ({
  widgetConfig,
  onRefresh,
  onOpenSettings,
  onRemove,
}: DashboardWidgetRendererProps) => {
  const definition = widgetRegistry.get(widgetConfig.definitionId);
  const globalFilters = useGlobalFilters();
  const { refreshTokens } = useRefreshContext();

  if (!definition) {
    return (
      <div className="widget-card">
        <div className="widget-error">
          Widget definition not found: {widgetConfig.definitionId}
        </div>
      </div>
    );
  }

  const resolvedSettings = resolveWidgetSettings(
    widgetConfig.definitionId,
    widgetConfig.settings
  );

  const dataState = definition.useData({
    settings: resolvedSettings,
    globalFilters,
    refreshToken: refreshTokens[widgetConfig.widgetId] ?? 0,
  });


  const widgetInstance: WidgetInstance = {
    definition,
    widgetId: widgetConfig.widgetId,
    settings: resolvedSettings,
    dataState,
  };

  return (
    <WidgetContainer
      widget={widgetInstance}
      onRefresh={onRefresh}
      onOpenSettings={onOpenSettings}
      onRemove={onRemove}
    />
  );
};

const sanitizeLayout = (layout: GridLayout[]): GridLayout[] => {
  return layout.map((item) => {
    const sanitized: GridLayout = {
      i: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
    };

    if (item.minW !== undefined && item.minW > 0) {
      sanitized.minW = Math.min(item.minW, item.w);
    }
    if (item.minH !== undefined && item.minH > 0) {
      sanitized.minH = Math.min(item.minH, item.h);
    }
    if (item.static !== undefined) {
      sanitized.static = item.static;
    }

    return sanitized;
  });
};

const adjustLayoutForSize = (layout: GridLayout[], cols: number): GridLayout[] => {
  return layout.map((item) => {
    const adjustedW = Math.min(item.w, cols);
    const adjustedMinW = item.minW ? Math.min(item.minW, adjustedW) : undefined;
    const adjustedMinH = item.minH ? Math.min(item.minH, item.h) : undefined;

    const result: GridLayout = {
      i: item.i,
      x: 0,
      y: item.y,
      w: adjustedW,
      h: item.h,
    };

    if (adjustedMinW !== undefined && adjustedMinW > 0) {
      result.minW = adjustedMinW;
    }
    if (adjustedMinH !== undefined && adjustedMinH > 0) {
      result.minH = adjustedMinH;
    }
    if (item.static !== undefined) {
      result.static = item.static;
    }

    return result;
  });
};

export default Dashboard;